/**
 * OnboardingContext — global gate for the "new/reactivated employee must
 * complete onboarding before unlocking operational sections" feature.
 *
 * Wraps the authenticated app. Provides:
 *   - loading:   true while the first status fetch is in-flight
 *   - required:  true when the current user is subject to the gate
 *                (i.e. new employee or reactivated after suspension). Legacy
 *                active employees are grandfathered → `required=false`.
 *   - complete:  true when all four onboarding checks pass
 *   - checks:    { profile, documents, policies, terms } — each with
 *                `complete: bool` + a `missing` list where applicable
 *   - refresh(): manual re-fetch after profile save / doc upload / ack
 *   - acceptTerms(): POST /api/onboarding/accept-terms + refresh
 *   - locked:    convenience — true when required AND not complete
 *
 * Route/nav consumers use `<useOnboarding()>` to decide whether to lock
 * sidebar items and redirect blocked routes.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ONBOARDING_LOCK_MESSAGE } from '../lib/onboarding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Re-export so existing importers of `ONBOARDING_LOCK_MESSAGE` from this
// module keep working without a codemod.
export { ONBOARDING_LOCK_MESSAGE };

const OnboardingContext = createContext({
  loading: true,
  required: false,
  complete: false,
  locked: false,
  checks: null,
  refresh: async () => {},
  acceptTerms: async () => {},
});

export function OnboardingProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    required: false,
    complete: false,
    checks: null,
    completedAt: null,
  });

  const fetchStatus = useCallback(async () => {
    // Onboarding gate only applies to employees + supervisors. Admins never
    // hit the endpoint — they always have full access.
    if (!user || (user.role !== 'employee' && user.role !== 'supervisor')) {
      setState({
        loading: false,
        required: false,
        complete: true,
        checks: null,
        completedAt: null,
      });
      return;
    }
    try {
      const { data } = await axios.get(`${API}/onboarding/status`);
      setState({
        loading: false,
        required: !!data.required,
        complete: !!data.complete,
        checks: data.checks || null,
        completedAt: data.completed_at || null,
      });
    } catch (err) {
      // On error, fail OPEN — don't lock the user out if the endpoint is
      // temporarily unreachable. Backend is the source of truth and will
      // re-arm the gate on the next successful fetch.
      console.warn('Onboarding status fetch failed:', err);
      setState({
        loading: false,
        required: false,
        complete: true,
        checks: null,
        completedAt: null,
      });
    }
  }, [user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const acceptTerms = useCallback(async () => {
    const { data } = await axios.post(`${API}/onboarding/accept-terms`);
    setState({
      loading: false,
      required: !!data.required,
      complete: !!data.complete,
      checks: data.checks || null,
      completedAt: data.completed_at || null,
    });
    return data;
  }, []);

  const value = useMemo(
    () => ({
      loading: state.loading,
      required: state.required,
      complete: state.complete,
      checks: state.checks,
      completedAt: state.completedAt,
      // "locked" = the gate is active for this user right now. All non-
      // allowed sections should be disabled while this is true.
      locked: state.required && !state.complete,
      refresh: fetchStatus,
      acceptTerms,
    }),
    [state, fetchStatus, acceptTerms],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  return useContext(OnboardingContext);
}
